import { useTranslation } from "react-i18next";
import { cx } from "../cx";
import { CloseIcon } from "../icons";

interface ChipBase {
  label: string;
  tone?: "neutral" | "accent";
  selected?: boolean;
  className?: string;
}

/**
 * A Chip is either clickable or removable, never both. The clickable chip is a
 * `<button>` around the whole tag and the remove control is a `<button>` inside
 * it, so allowing the pair would nest one button in the other: invalid HTML
 * that React refuses to hydrate and that the parser splits into two elements.
 * The combination is ruled out in the type rather than at runtime, so it cannot
 * be written in the first place.
 */
export type ChipProps = ChipBase &
  (
    | { onClick?: never; onRemove?: never }
    | { onClick: () => void; onRemove?: never }
    | { onClick?: never; onRemove: () => void }
  );

/** A small mono tag: filter facets on Casting ("English", "Narration"), removable terms. */
export function Chip({
  label,
  tone = "neutral",
  selected = false,
  onClick,
  onRemove,
  className = "",
}: ChipProps) {
  const { t } = useTranslation("components");
  const accent = tone === "accent" || selected;
  const classes = cx(
    "inline-flex h-[22px] items-center gap-1 rounded-sm border px-2 font-mono text-label",
    accent ? "border-accent text-accent" : "border-line text-ink-2",
    onClick && "hover:bg-sunken",
    className,
  );
  const body = (
    <>
      <span>{label}</span>
      {onRemove ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          aria-label={t("chip.remove", { label })}
          className="-mr-1 inline-flex h-4 w-4 items-center justify-center rounded-sm hover:bg-line-soft"
        >
          <CloseIcon size={10} />
        </button>
      ) : null}
    </>
  );
  return onClick ? (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      data-component="Chip"
      className={classes}
    >
      {body}
    </button>
  ) : (
    <span data-component="Chip" className={classes}>
      {body}
    </span>
  );
}
